import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { HTMLArtifact, ConversationState, ThreadConversationAPIResponse } from '../types/api';
import { htmlEditorAPI } from '../services/api';
import HTMLViewer from './HTMLViewer';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  TextField,
  Paper,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Backdrop,
  Fade,
  LinearProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Publish as PublishIcon,
  Send as SendIcon,
  SmartToy as SmartToyIcon,
  Person as PersonIcon,
  ArrowForward as ArrowForwardIcon,
  Cached as RegenerateIcon,
  MedicalServices as DiagnosticsIcon,
  Public as PublicIcon
} from '@mui/icons-material';

interface Message {
  id: string;
  type: 'user' | 'ai' | 'system';
  content: string;
  timestamp: number;
  intent?: 'html_edit' | 'conversation';
  isStreaming?: boolean;
}

interface HTMLEditorChatbotProps {
  threadId?: string;
}

// Memoized Message Component for Performance
const MessageComponent = React.memo<{ 
  message: Message; 
}>(({ message }) => {
  // Determine if this is an HTML-related system message
  const isHtmlChange = message.type === 'system' && (
    message.intent === 'html_edit' || 
    message.content.includes('HTML changes') ||
    message.content.includes('HTML has been updated') ||
    message.content.includes('HTML content')
  );

  return (
    <Box sx={{
      padding: '8px 12px',
      borderRadius: '6px',
      background: message.type === 'user' 
        ? '#555' 
        : isHtmlChange 
          ? 'linear-gradient(135deg, rgba(240, 85, 2, 0.15) 0%, rgba(240, 85, 2, 0.08) 100%)'
          : '#333',
      border: isHtmlChange ? '1px solid rgba(240, 85, 2, 0.3)' : 'none',
      color: message.type === 'user' ? 'white' : '#FFFFFF',
      mb: 1
    }}>
      <Typography variant="caption" sx={{ 
        opacity: 0.8, 
        fontSize: '11px',
        fontFamily: 'Inter, sans-serif',
        color: isHtmlChange ? '#F05502' : 'inherit'
      }}>
        {message.type === 'user' ? 'You' : message.type === 'system' ? 'System' : 'AI'}
      </Typography>
        
    {message.type === 'ai' ? (
      <Box sx={{
        fontSize: '12px',
        lineHeight: 1.3,
        fontFamily: 'Inter, sans-serif',
        mt: 0.5,
        '& p': {
          margin: '4px 0',
          fontSize: '12px',
          color: 'inherit'
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          margin: '8px 0 4px 0',
          fontSize: '13px',
          fontWeight: 'bold',
          color: 'inherit'
        },
        '& ul, & ol': {
          margin: '4px 0',
          paddingLeft: '16px'
        },
        '& li': {
          margin: '2px 0',
          fontSize: '12px'
        },
        '& code': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          padding: '2px 4px',
          borderRadius: '3px',
          fontSize: '11px',
          fontFamily: 'monospace'
        },
        '& pre': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          padding: '8px',
          borderRadius: '4px',
          overflow: 'auto',
          margin: '4px 0'
        },
        '& blockquote': {
          borderLeft: '3px solid #666',
          paddingLeft: '8px',
          margin: '4px 0',
          fontStyle: 'italic'
        },
        '& strong': {
          fontWeight: 'bold'
        },
        '& em': {
          fontStyle: 'italic'
        }
      }}>
        <ReactMarkdown>{message.content}</ReactMarkdown>
      </Box>
    ) : (
      <Typography variant="body2" sx={{
        fontSize: '12px',
        lineHeight: 1.3,
        fontFamily: 'Inter, sans-serif',
        mt: 0.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
      }}>
        {message.content}
      </Typography>
         )}
   </Box>
 );
});

const HTMLEditorChatbot: React.FC<HTMLEditorChatbotProps> = ({ threadId: propThreadId }) => {
  // State variables
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [artifacts, setArtifacts] = useState<HTMLArtifact[]>([]);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  
  // URL and thread management
  const [threadId, setThreadId] = useState<string>('');
  const [urlParamsChecked, setUrlParamsChecked] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Publish modal state
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishState, setPublishState] = useState<'confirm' | 'publishing' | 'published'>('confirm');
  const [publishedUrl, setPublishedUrl] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  
  // Published page URL from conversation API
  const [publishedPageUrl, setPublishedPageUrl] = useState<string | null>(null);

  // Diagnostics confirmation & running state
  const [diagnosticsModalOpen, setDiagnosticsModalOpen] = useState(false);
  const [diagnosticsRunning, setDiagnosticsRunning] = useState(false);
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('info');
  const [htmlRenderCounter, setHtmlRenderCounter] = useState(0);

  const theme = useTheme();
  const abortControllerRef = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Helper functions first
  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' | 'info' = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showSnackbar('Copied to clipboard!', 'success');
    } catch (error) {
      showSnackbar('Failed to copy to clipboard', 'error');
    }
  }, [showSnackbar]);

  // Hotkeys for ⌘+R (Regenerate) and ⌘+D (Diagnostics)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey && !e.shiftKey && !e.altKey) {
        if (e.key.toLowerCase() === 'r') {
          e.preventDefault();
          handleRegenerateClick();
        } else if (e.key.toLowerCase() === 'd') {
          e.preventDefault();
          handleDiagnosticsClick();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [threadId]);

  // Regenerate handler – redirect to creation page
  const handleRegenerateClick = useCallback(() => {
    window.open('https://topmate.io/sites/service-selection', '_blank');
  }, []);

  // Diagnostics click opens confirmation modal
  const handleDiagnosticsClick = useCallback(() => {
    if (diagnosticsRunning) return;
    setDiagnosticsModalOpen(true);
  }, [diagnosticsRunning]);

  const handleDiagnosticsConfirm = useCallback(async () => {
    if (diagnosticsRunning) return;
    setDiagnosticsRunning(true);
    try {
      // Check if we have HTML content to fix
      if (!htmlContent || !htmlContent.trim()) {
        showSnackbar('No HTML content to fix', 'info');
        return;
      }

      // Call the fix HTML API
      const result = await htmlEditorAPI.fixHTML(htmlContent);
      
      if (result.success) {
        // Update the HTML content with the fixed version
        setHtmlContent(result.fixed_html);
        
        // Create a new artifact for the fixed HTML
        const fixedArtifact: HTMLArtifact = {
          id: `fixed-${Date.now()}`,
          content: result.fixed_html,
          isValid: true,
          errors: [],
          warnings: [],
          timestamp: Date.now()
        };
        setArtifacts(prev => [...prev, fixedArtifact]);
        
        // Add a system message about the fix
        const fixMessage: Message = {
          id: `fix-${Date.now()}`,
          type: 'system',
          content: `🔧 HTML diagnostics completed and fixed (${result.original_length} → ${result.fixed_length} characters)`,
          timestamp: Date.now(),
          intent: 'html_edit',
          isStreaming: false
        };
        setMessages(prev => [...prev, fixMessage]);
        
        // Update thread HTML with the fixed version
        try {
          await htmlEditorAPI.updateThreadHTML(threadId, result.fixed_html);
        } catch (error) {
          console.warn('Failed to update thread HTML:', error);
        }
        
        showSnackbar('HTML diagnostics completed and issues fixed!', 'success');
      } else {
        showSnackbar(result.error || 'Failed to fix HTML issues', 'error');
      }
    } catch (error) {
      console.error('Diagnostics error:', error);
      showSnackbar('Diagnostics failed: ' + (error instanceof Error ? error.message : 'Unknown error'), 'error');
    } finally {
      setDiagnosticsRunning(false);
      setDiagnosticsModalOpen(false);
    }
  }, [diagnosticsRunning, threadId, htmlContent, showSnackbar]);

  const handleDiagnosticsClose = useCallback(() => {
    if (diagnosticsRunning) return;
    setDiagnosticsModalOpen(false);
  }, [diagnosticsRunning]);

  // Read thread_id from URL or prop
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const threadIdFromUrl = urlParams.get('thread_id');
    
    if (propThreadId && propThreadId.trim()) {
      setThreadId(propThreadId.trim());
    } else if (threadIdFromUrl && threadIdFromUrl.trim()) {
      setThreadId(threadIdFromUrl.trim());
    } else {
      setThreadId('default');
    }
    
    setUrlParamsChecked(true);
  }, [propThreadId]);

  // Load initial data
  useEffect(() => {
    if (!urlParamsChecked || !threadId) return;

    const loadInitialData = async () => {
      try {
        setIsInitializing(true);
        let currentHtmlFromConversation = '';
        
        // Load conversation history
        const conversationResponse: ThreadConversationAPIResponse = await htmlEditorAPI.getThreadConversation(threadId);
        if (conversationResponse.success && conversationResponse.conversation_data) {
          const { messages: historyMessages, current_html } = conversationResponse.conversation_data;
          currentHtmlFromConversation = current_html || '';
          
          // Extract published page URL from conversation response
          if (conversationResponse.published_page_url) {
            setPublishedPageUrl(conversationResponse.published_page_url);
          }
          
          if (historyMessages && Array.isArray(historyMessages)) {
            const formattedMessages: Message[] = historyMessages.map(msg => {
              const baseMessage = {
                id: msg.id || `msg-${Date.now()}-${Math.random()}`,
                type: msg.type as 'user' | 'ai' | 'system',
                content: msg.content || '',
                timestamp: msg.timestamp || Date.now(),
                intent: msg.metadata?.intent || 'conversation',
                isStreaming: false
              };

              // If this is an AI message, check if it contains HTML and replace with simplified message
              if (baseMessage.type === 'ai' && baseMessage.content) {
                const htmlDetection = detectAndExtractHtml(baseMessage.content);
                if (htmlDetection.hasHtml && htmlDetection.htmlContent) {
                  return {
                    ...baseMessage,
                    type: 'system' as const,
                    content: '🎨 HTML changes applied to the page',
                    intent: 'html_edit' as const
                  };
                }
              }

              return baseMessage;
            });
            setMessages(formattedMessages);
          }

          if (currentHtmlFromConversation) {
            setHtmlContent(currentHtmlFromConversation);
            // Create artifact for current HTML
            setArtifacts([{
              id: 'current',
              content: currentHtmlFromConversation,
              isValid: true,
              errors: [],
              warnings: [],
              timestamp: Date.now()
            }]);
          }
        }

        // Load initial HTML only if no current HTML was found in conversation
        if (!currentHtmlFromConversation) {
          const htmlResponse = await htmlEditorAPI.getThreadHTML(threadId);
          if (htmlResponse.success && htmlResponse.html_content) {
            setHtmlContent(htmlResponse.html_content);
            setArtifacts([{
              id: 'initial',
              content: htmlResponse.html_content,
              isValid: true,
              errors: [],
              warnings: [],
              timestamp: Date.now()
            }]);
          }
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        setMessages(prev => [...prev, {
          id: `error-${Date.now()}`,
          type: 'system',
          content: 'Failed to load conversation history. Starting fresh.',
          timestamp: Date.now(),
          isStreaming: false
        }]);
        setError(error instanceof Error ? error.message : 'Failed to load data');
      } finally {
        setIsInitializing(false);
      }
    };

    loadInitialData();
  }, [threadId, urlParamsChecked]);

  // Auto-scroll when messages change
  useEffect(() => {
    if (!isStreaming) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages, isStreaming, scrollToBottom]);

  // Debug: Log when htmlContent changes and force re-render (throttled)
  useEffect(() => {
    if (htmlContent.length > 0) {
      console.log('🔄 HTML Content Updated:', {
        length: htmlContent.length,
        timestamp: Date.now()
      });
    }
    // Force HTMLViewer re-render by incrementing counter
    setHtmlRenderCounter(prev => prev + 1);
  }, [htmlContent]);

  // Debug: Track loading states (throttled for performance)
  useEffect(() => {
    // Only log significant state changes, not every minor update
    if (isLoading || isStreaming) {
      console.log('🔄 Loading States Changed:', {
        isLoading,
        isStreaming,
        showLoadingOverlay,
        timestamp: Date.now()
      });
    }
  }, [isLoading, isStreaming, showLoadingOverlay]);

  // Warn user before leaving page if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Only show warning if there's HTML content (indicating work that could be lost)
      if (htmlContent && htmlContent.trim()) {
        const message = 'You have unsaved changes that will be lost. Consider publishing your page before leaving.';
        event.preventDefault();
        event.returnValue = message; // Standard way to trigger browser confirmation
        return message;
      }
    };

    // Add event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [htmlContent]);

  // Send message handler
  const handleSendMessage = async () => {
    if (!currentMessage.trim() || !threadId || isLoading) {
      return;
    }

    const currentRequestId = `req-${Date.now()}-${Math.random()}`;
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: currentMessage.trim(),
      timestamp: Date.now(),
      isStreaming: false
    };

    // Force immediate state reset first
    setShowLoadingOverlay(false);
    setIsLoading(false);
    setIsStreaming(false);
    setRequestId(null);
    
    // Small delay to ensure state reset is processed
    await new Promise(resolve => setTimeout(resolve, 10));
    
    // Use React.startTransition for immediate state updates
    React.startTransition(() => {
      setMessages(prev => [...prev, userMessage]);
      setCurrentMessage('');
      setRequestId(currentRequestId);
      setShowLoadingOverlay(true);
      setIsLoading(true);
      setIsStreaming(true);
    });
    
    // Additional safeguard: Force another overlay check after transition
    setTimeout(() => {
      if (!showLoadingOverlay) {
        setShowLoadingOverlay(true);
      }
    }, 20);

    // Abort any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    try {
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      let aiResponseContent = '';
      let currentIntent: 'html_edit' | 'conversation' = 'conversation';

      const onMessage = (data: any) => {
        if (abortController.signal.aborted) return;

        const eventType = data.type;
        const content = data.content || '';
        const metadata = data.metadata || {};

        switch (eventType) {
          case 'status':
            if (content.includes('🎨 Generating HTML content')) {
              currentIntent = 'html_edit';
              showSnackbar('🎨 Generating HTML content...', 'info');
            } else if (content.includes('✅ HTML content updated')) {
              showSnackbar('✅ HTML content updated!', 'success');
            }
            break;

          case 'ai_message':
            currentIntent = metadata.intent || 'conversation';
            aiResponseContent = content;

            console.log('📤 AI Message Event:', {
              intent: currentIntent,
              contentLength: content.length,
              hasHtml: content.includes('<html'),
              metadata: metadata
            });

            // Always check for HTML content first, regardless of intent
            const htmlDetection = detectAndExtractHtml(content);
            
            if (htmlDetection.hasHtml && htmlDetection.htmlContent) {
              // HTML detected - stream to canvas, don't add to chat messages
              console.log('🎨 Streaming HTML to canvas...', htmlDetection.htmlContent.length);
              console.log('🎨 Setting HTML content via ai_message event, isStreaming:', isStreaming);
              setHtmlContent(htmlDetection.htmlContent);
              
              const newArtifact: HTMLArtifact = {
                id: `artifact-ai-${Date.now()}`,
                content: htmlDetection.htmlContent,
                isValid: true,
                errors: [],
                warnings: [],
                timestamp: Date.now()
              };
              setArtifacts(prev => [...prev, newArtifact]);
              
              // Add a brief system message indicating HTML was updated
                              const htmlUpdateMessage: Message = {
                  id: `system-${Date.now()}`,
                  type: 'system',
                  content: '🎨 HTML changes applied to the page',
                  timestamp: Date.now(),
                  intent: 'html_edit',
                  isStreaming: false
                };
              setMessages(prev => [...prev, htmlUpdateMessage]);
              
              console.log('✅ HTML content streamed to canvas, no chat message added');
            } else {
              // No HTML detected - this is a regular conversation message
              // Truncate content to 1000 characters for chat display
              const truncatedContent = aiResponseContent.length > 1000 
                ? aiResponseContent.substring(0, 1000) + '...' 
                : aiResponseContent;
              
              const aiMessage: Message = {
                id: `ai-${Date.now()}`,
                type: 'ai',
                content: truncatedContent,
                timestamp: Date.now(),
                intent: currentIntent,
                isStreaming: false
              };
              setMessages(prev => [...prev, aiMessage]);
              
              console.log('💬 Added conversation message to chat (no HTML detected), truncated:', aiResponseContent.length > 1000);
            }
            break;

          case 'html_content':
            console.log('📄 HTML Content Event:', {
              contentLength: content.length,
              hasHtml: content.includes('<html'),
              metadata: metadata
            });
            
            // Update HTML content and create artifact
            console.log('🎨 Setting HTML content via html_content event, isStreaming:', isStreaming);
            setHtmlContent(content);
            
            const newArtifact: HTMLArtifact = {
              id: `artifact-${Date.now()}`,
              content: content,
              isValid: true,
              errors: [],
              warnings: [],
              timestamp: Date.now()
            };
            setArtifacts(prev => [...prev, newArtifact]);
            
            console.log('🎨 HTML content set via html_content event, length:', content.length);
            
            // Add a system message indicating HTML was updated
            const htmlUpdateMessage: Message = {
              id: `system-${Date.now()}`,
              type: 'system',
              content: '🎨 HTML changes applied to the page',
              timestamp: Date.now(),
              intent: 'html_edit',
              isStreaming: false
            };
            setMessages(prev => [...prev, htmlUpdateMessage]);
            break;

          case 'error':
            const errorMessage: Message = {
              id: `error-${Date.now()}`,
              type: 'system',
              content: `❌ Error: ${content}`,
              timestamp: Date.now(),
              isStreaming: false
            };
            setMessages(prev => [...prev, errorMessage]);
            showSnackbar('Error processing request', 'error');
            break;
        }
      };

      const onError = (error: Error) => {
        if (abortController.signal.aborted) return;
        
        console.error('Streaming error:', error);
        
        // Reset all loading states
        setShowLoadingOverlay(false);
        setIsLoading(false);
        setIsStreaming(false);
        setRequestId(null);
        
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          type: 'system',
          content: `❌ Connection error: ${error.message}`,
          timestamp: Date.now(),
          isStreaming: false
        };
        setMessages(prev => [...prev, errorMessage]);
        showSnackbar('Connection error occurred', 'error');
      };

      const onComplete = () => {
        if (abortController.signal.aborted) return;
        // Use a small delay to ensure the overlay was visible long enough for good UX
        setTimeout(() => {
          setShowLoadingOverlay(false);
          setIsLoading(false);
          setIsStreaming(false);
          setRequestId(null);
        }, 200); // Minimum 200ms delay for smooth UX
      };

      // Start streaming
      const cleanup = htmlEditorAPI.streamEditHTML(
        {
          user_message: userMessage.content,
          thread_id: threadId,
          conversation_id: undefined
        },
        onMessage,
        onError,
        onComplete
      );

      abortControllerRef.current = {
        abort: () => {
          cleanup();
          abortController.abort();
          setShowLoadingOverlay(false);
          setIsLoading(false);
          setIsStreaming(false);
          setRequestId(null);
        }
      } as AbortController;

    } catch (error) {
      console.error('Error in handleSendMessage:', error);
      
      // Reset all loading states
      setShowLoadingOverlay(false);
      setIsLoading(false);
      setIsStreaming(false);
      setRequestId(null);
      
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        type: 'system',
        content: `❌ Failed to send message: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: Date.now(),
        isStreaming: false
      };
      setMessages(prev => [...prev, errorMessage]);
      showSnackbar('Failed to send message', 'error');
    }
  };

  const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper function to detect and extract HTML content from AI responses
  const detectAndExtractHtml = (content: string): { hasHtml: boolean; htmlContent: string | null } => {
    if (!content) return { hasHtml: false, htmlContent: null };

    // Patterns to detect HTML content
    const htmlPatterns = [
      /```html\s*([\s\S]*?)```/i,           // HTML in markdown code blocks
      /```\s*(<!DOCTYPE html>[\s\S]*?)```/i, // HTML with DOCTYPE in code blocks  
      /(<!DOCTYPE html>[\s\S]*)/i,          // Direct HTML with DOCTYPE
      /(<html[\s\S]*<\/html>)/i,            // HTML tags
      /(<html[^>]*>[\s\S]*)/i,              // HTML opening tag
    ];

    // Additional simple patterns for HTML detection
    const simpleHtmlPatterns = [
      /<!DOCTYPE html>/i,
      /<html[\s>]/i,
      /<head>/i,
      /<body[\s>]/i,
      /<div[\s>]/i,
      /<style[\s>]/i,
      /<script[\s>]/i
    ];

    // Check if content contains HTML patterns
    const hasHtmlPattern = simpleHtmlPatterns.some(pattern => pattern.test(content));
    
    if (!hasHtmlPattern) {
      return { hasHtml: false, htmlContent: null };
    }

    // Try to extract HTML content from various formats
    for (const pattern of htmlPatterns) {
      const match = content.match(pattern);
      if (match) {
        let extractedHtml = match[1] || match[0];
        
        // Clean up the extracted HTML
        extractedHtml = extractedHtml.trim();
        
        // Ensure it starts with DOCTYPE or html tag
        if (extractedHtml.includes('<!DOCTYPE html>') || extractedHtml.includes('<html')) {
          return { hasHtml: true, htmlContent: extractedHtml };
        }
      }
    }

    // If we detected HTML patterns but couldn't extract properly, 
    // try using the whole content if it looks like HTML
    if (content.includes('<!DOCTYPE html>') || content.includes('<html')) {
      // Remove any leading/trailing markdown syntax
      let cleanContent = content
        .replace(/^```html\s*/i, '')
        .replace(/```\s*$/i, '')
        .trim();
      
      return { hasHtml: true, htmlContent: cleanContent };
    }

    return { hasHtml: false, htmlContent: null };
  };

  // Publish handlers
  const handlePublishClick = useCallback(() => {
    setPublishModalOpen(true);
    setPublishState('confirm');
    setPublishedUrl(null);
    setPublishError(null);
  }, []);

  const handlePublishConfirm = useCallback(async () => {
    if (!threadId) return;
    
    setPublishState('publishing');
    setPublishError(null);
    
    try {
      const result = await htmlEditorAPI.publishHTML(threadId);
      
      if (result.success) {
        // Prefer published_page_url, fallback to published_url
        const finalUrl = result.published_page_url || result.published_url || null;
        setPublishedUrl(finalUrl);
        setPublishState('published');
        showSnackbar('HTML published successfully!', 'success');
      } else {
        setPublishError(result.message || 'Publishing failed');
        showSnackbar('Publishing failed', 'error');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setPublishError(errorMessage);
      showSnackbar('Publishing failed', 'error');
    }
  }, [threadId, showSnackbar]);

  const handlePublishClose = useCallback(() => {
    setPublishModalOpen(false);
    setPublishState('confirm');
    setPublishedUrl(null);
    setPublishError(null);
  }, []);

  const handlePreviewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'desktop' | 'mobile') => {
    if (newMode !== null) {
      setPreviewMode(newMode);
    }
  };

  // Memoize messages rendering for performance
  const renderedMessages = useMemo(() => {
    return messages.map((message) => (
      <MessageComponent 
        key={message.id} 
        message={message} 
      />
    ));
  }, [messages]);

  // Memoize TextField props to prevent unnecessary re-renders
  const textFieldProps = useMemo(() => ({
    fullWidth: true,
    multiline: true,
    rows: 3,
    placeholder: "Describe your changes...",
    variant: "outlined" as const,
    sx: {
      '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        background: '#0D0D0D',
        border: '1px solid #333',
        color: '#FFFFFF',
        fontFamily: 'Inter, sans-serif',
        fontSize: '14px',
        padding: '12px',
        '& fieldset': {
          borderColor: '#333',
        },
        '&:hover fieldset': {
          borderColor: '#666',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#666',
        },
        '& textarea': {
          color: '#FFFFFF',
          '&::placeholder': {
            color: '#666',
            opacity: 1,
          }
        }
      },
      '& .MuiInputBase-input::placeholder': {
        color: '#666',
        opacity: 1,
      },
    }
  }), []);

  // Loading states
  if (isInitializing || !urlParamsChecked) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading conversation...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" flexDirection="column">
        <Typography variant="h6" color="error" gutterBottom>
          Error loading conversation
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" height="100vh" sx={{ background: '#0D0D0D' }}>
      {/* Header */}
      <Box sx={{
        height: '64px',
        background: '#1A1A1A',
        borderBottom: '1px solid #2A2A2A',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px'
      }}>
        {/* Left - Logo and Create New Page Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: '#F05502',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '16px'
            }}>
              T
            </Box>
            <Typography sx={{
              color: '#FFFFFF',
              fontSize: '16px',
              fontWeight: '500',
              fontFamily: 'Inter, sans-serif'
            }}>
              topmate
            </Typography>
          </Box>
          
          <Button
            onClick={() => window.open('https://topmate.io/sites/service-selection', '_blank')}
            sx={{
              color: '#999',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: '400',
              textTransform: 'none',
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #333',
              background: 'transparent',
              '&:hover': {
                background: '#2A2A2A',
                color: '#FFFFFF',
                borderColor: '#555',
              },
            }}
          >
            Create new page
          </Button>
        </Box>

        {/* Center - All Controls in One Line */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Preview/Code Toggle */}
          <ToggleButtonGroup
            value={previewMode}
            exclusive
            onChange={handlePreviewModeChange}
            sx={{
              '& .MuiToggleButton-root': {
                border: '1px solid #444',
                color: '#999',
                fontFamily: 'Inter, sans-serif',
                textTransform: 'none',
                padding: '8px 16px',
                '&.Mui-selected': {
                  backgroundColor: '#555',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#666',
                  }
                },
                '&:hover': {
                  backgroundColor: '#333',
                }
              }
            }}
          >
            <ToggleButton value="desktop">Desktop</ToggleButton>
            <ToggleButton value="mobile">Mobile</ToggleButton>
          </ToggleButtonGroup>

          {/* Toolbar Buttons */}
          <Button
            variant="contained"
            onClick={handlePublishClick}
            disabled={!threadId}
            sx={{
              background: '#F05502',
              fontFamily: 'Inter, sans-serif',
              fontWeight: '600',
              borderRadius: '8px',
              textTransform: 'none',
              padding: '8px 20px',
              '&:hover': {
                background: '#e04a02',
              },
              '&:disabled': {
                background: '#444',
                color: '#999',
              },
            }}
          >
            Publish
          </Button>

          {/* Run Diagnostics */}
          <Tooltip title="Fix HTML Issues (⌘+D)">
            <IconButton onClick={handleDiagnosticsClick} disabled={diagnosticsRunning} sx={{ color: '#999' }}>
              <DiagnosticsIcon />
            </IconButton>
          </Tooltip>

          {/* Copy Link */}
          <Tooltip title="Copy Page Link">
            <IconButton onClick={() => copyToClipboard(publishedPageUrl || window.location.href)} sx={{ color: '#999' }}>
              <CopyIcon />
            </IconButton>
          </Tooltip>

          {/* Open Live Page */}
          <Tooltip title="Open Live Page">
            <span>
              <IconButton onClick={() => publishedUrl && window.open(publishedUrl, '_blank')} disabled={!publishedUrl} sx={{ color: publishedUrl ? '#999' : '#555' }}>
                <PublicIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Right - Feedback Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: 100 }}>
          <Button
            onClick={() => window.open('https://topmate.click/sites-feedback', '_blank')}
            sx={{
              color: '#999',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: '400',
              textTransform: 'none',
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #333',
              background: 'transparent',
              '&:hover': {
                background: '#2A2A2A',
                color: '#FFFFFF',
                borderColor: '#555',
              },
            }}
          >
            Feedback
          </Button>
        </Box>
      </Box>

      {/* Main Content Area */}
      <Box display="flex" flex="1" overflow="hidden">
        {/* Left Panel */}
        <Box sx={{
          width: '380px',
          minWidth: '380px',
          maxWidth: '380px',
          flexShrink: 0,
          height: '100%',
          background: '#1A1A1A',
          borderRight: '1px solid #2A2A2A',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Welcome Message - Fixed at top */}
          <Box sx={{ padding: '40px 32px 24px 32px' }}>
            <Typography sx={{
              color: '#FFFFFF',
              fontSize: '20px',
              fontWeight: '600',
              fontFamily: 'Inter, sans-serif',
              lineHeight: 1.3,
              mb: 2
            }}>
              Welcome to the topmate<br />sites editor
            </Typography>
            
            <Typography sx={{
              color: '#999',
              fontSize: '14px',
              fontFamily: 'Inter, sans-serif',
              mb: 2
            }}>
              What do you want to edit in the page
            </Typography>
            
            <Typography sx={{
              color: '#666',
              fontSize: '13px',
              fontFamily: 'Inter, sans-serif',
              fontStyle: 'italic'
            }}>
              💡 To save your edits click on publish
            </Typography>
          </Box>

          {/* Messages History - Flexible middle area */}
          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            padding: '0 32px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#333',
              borderRadius: '2px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#666',
              borderRadius: '2px',
            },
          }}>
            {messages.length === 0 ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                mt: 4,
                gap: 3,
                color: '#CCC',
                textAlign: 'center'
              }}>
                {/* 60-sec Tour */}
                <Button
                  startIcon={<SmartToyIcon />}
                  onClick={() => window.open('https://youtu.be/f8tOjsO6jgQ?si=Cx0E0fip3UB-BX9P', '_blank')}
                  sx={{
                    color: '#F05502',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '14px'
                  }}
                >
                  Watch a 60-sec tour ▶︎
                </Button>

                {/* Primary & Secondary CTAs */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<RegenerateIcon />}
                    onClick={handleRegenerateClick}
                    sx={{
                      background: '#F05502',
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Regenerate Page
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DiagnosticsIcon />}
                    onClick={handleDiagnosticsClick}
                    disabled={diagnosticsRunning}
                    sx={{
                      borderColor: '#555',
                      color: '#CCC',
                      textTransform: 'none',
                      '&:hover': { borderColor: '#777' }
                    }}
                  >
                    Fix HTML Issues
                  </Button>
                </Box>

                {/* Utility buttons */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="text"
                    startIcon={<CopyIcon />}
                    onClick={() => copyToClipboard(publishedPageUrl || window.location.href)}
                    sx={{ color: '#AAA', textTransform: 'none' }}
                  >
                    Copy link
                  </Button>
                  <Button
                    variant="text"
                    startIcon={<PublicIcon />}
                    onClick={() => publishedUrl && window.open(publishedUrl, '_blank')}
                    disabled={!publishedUrl}
                    sx={{ color: publishedUrl ? '#AAA' : '#555', textTransform: 'none' }}
                  >
                    Open live page
                  </Button>
                </Box>
              </Box>
            ) : (
              renderedMessages
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area - Fixed at bottom */}
          <Box sx={{
            padding: '16px 32px 32px 32px',
            borderTop: messages.length > 0 ? '1px solid #2A2A2A' : 'none'
          }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                {...textFieldProps}
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
              />
              <IconButton
                onClick={handleSendMessage}
                disabled={!currentMessage.trim() || isLoading}
                sx={{
                  backgroundColor: '#555',
                  color: 'white',
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  alignSelf: 'flex-end',
                  '&:hover': {
                    backgroundColor: '#666',
                  },
                  '&:disabled': {
                    backgroundColor: '#444',
                    color: '#999',
                  },
                }}
              >
                <ArrowForwardIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Canvas Area */}
        <Box sx={{
          flex: 1,
          height: '100%',
          background: '#0D0D0D',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <HTMLViewer
            key={`html-viewer-${htmlRenderCounter}`}
            htmlContent={htmlContent}
            artifacts={artifacts}
            threadId={threadId}
            isLoading={isLoading}
            isStreaming={isStreaming}
            onPublishClick={handlePublishClick}
            previewMode={previewMode}
          />
        </Box>
      </Box>

      {/* Beautiful Loading Overlay */}
      <Backdrop 
        open={showLoadingOverlay} 
        sx={{ 
          zIndex: 9999,
          backgroundColor: 'rgba(13, 13, 13, 0.95)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Fade in={showLoadingOverlay}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: 4,
              borderRadius: '20px',
              background: 'linear-gradient(135deg, rgba(240, 85, 2, 0.1) 0%, rgba(240, 85, 2, 0.05) 100%)',
              border: '1px solid rgba(240, 85, 2, 0.2)',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
              minWidth: '300px',
              maxWidth: '500px',
            }}
          >
            {/* Animated loading spinner */}
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <CircularProgress
                size={60}
                thickness={3}
                sx={{
                  color: '#F05502',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  },
                }}
              />
              {/* Inner pulsating circle */}
              <Box
                sx={{
                  position: 'absolute',
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  backgroundColor: '#F05502',
                  opacity: 0.3,
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'scale(0.8)',
                      opacity: 0.3,
                    },
                    '50%': {
                      transform: 'scale(1.2)',
                      opacity: 0.1,
                    },
                    '100%': {
                      transform: 'scale(0.8)',
                      opacity: 0.3,
                    },
                  },
                }}
              />
            </Box>

            {/* Loading text with animation */}
            <Typography
              sx={{
                color: '#FFFFFF',
                fontSize: '18px',
                fontWeight: '600',
                fontFamily: 'Inter, sans-serif',
                mb: 1,
                animation: 'fadeInOut 2s infinite',
                '@keyframes fadeInOut': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.7 },
                },
              }}
            >
              🎨 AI is working on your page
            </Typography>
            
            <Typography
              sx={{
                color: '#999',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                mb: 2,
                maxWidth: '350px',
                lineHeight: 1.4,
              }}
            >
              Creating beautiful HTML content based on your request...
            </Typography>

            {/* Animated progress bar */}
            <Box sx={{ width: '100%', maxWidth: '300px' }}>
              <LinearProgress
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#F05502',
                    borderRadius: 2,
                    background: 'linear-gradient(90deg, #F05502 0%, #FF6B35 50%, #F05502 100%)',
                    backgroundSize: '200% 100%',
                    animation: 'gradientShift 2s infinite',
                    '@keyframes gradientShift': {
                      '0%': {
                        backgroundPosition: '200% 0',
                      },
                      '100%': {
                        backgroundPosition: '-200% 0',
                      },
                    },
                  },
                }}
              />
            </Box>

            {/* Subtle tip text */}
            <Typography
              sx={{
                color: '#666',
                fontSize: '12px',
                fontFamily: 'Inter, sans-serif',
                mt: 3,
                fontStyle: 'italic',
                animation: 'breathe 3s infinite',
                '@keyframes breathe': {
                  '0%, 100%': { opacity: 0.6 },
                  '50%': { opacity: 1 },
                },
              }}
            >
              This usually takes a few seconds
            </Typography>
          </Box>
        </Fade>
      </Backdrop>

      {/* Publish Confirmation Modal */}
      <Dialog 
        open={publishModalOpen} 
        onClose={publishState !== 'publishing' ? handlePublishClose : undefined}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {publishState === 'confirm' && 'Publish HTML Content'}
          {publishState === 'publishing' && 'Publishing...'}
          {publishState === 'published' && 'Published Successfully!'}
          {publishState !== 'publishing' && (
            <IconButton
              aria-label="close"
              onClick={handlePublishClose}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        
        <DialogContent>
          {publishState === 'confirm' && (
            <Typography>
              Are you sure you want to publish the current HTML content? This will make it publicly accessible.
            </Typography>
          )}
          
          {publishState === 'publishing' && (
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={24} />
              <Typography>Publishing your HTML content...</Typography>
            </Box>
          )}
          
          {publishState === 'published' && publishedUrl && (
            <Box>
              <Typography gutterBottom>
                Your HTML content has been published successfully!
              </Typography>
              <Box 
                display="flex" 
                alignItems="center" 
                gap={1} 
                sx={{
                  p: 2,
                  bgcolor: theme.palette.grey[100],
                  borderRadius: 1,
                  mt: 2
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    wordBreak: 'break-all',
                    flex: 1,
                    fontFamily: 'monospace'
                  }}
                >
                  {publishedUrl}
                </Typography>
                <Tooltip title="Copy URL">
                  <IconButton 
                    size="small" 
                    onClick={() => copyToClipboard(publishedUrl)}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
          
          {publishError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {publishError}
            </Alert>
          )}
        </DialogContent>
        
        <DialogActions>
          {publishState === 'confirm' && (
            <>
              <Button onClick={handlePublishClose}>Cancel</Button>
              <Button 
                onClick={handlePublishConfirm}
                variant="contained"
                color="primary"
              >
                Publish
              </Button>
            </>
          )}
          
          {publishState === 'published' && (
            <Button onClick={handlePublishClose} variant="contained">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Diagnostics Confirmation Modal */}
      <Dialog 
        open={diagnosticsModalOpen}
        onClose={handleDiagnosticsClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Run Diagnostics?
          <IconButton
            aria-label="close"
            onClick={handleDiagnosticsClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            This will analyze and automatically fix HTML issues including structure problems, accessibility issues, contrast problems, and hidden interactive elements. The fixes will be applied to your current page. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDiagnosticsClose}>Cancel</Button>
          <Button onClick={handleDiagnosticsConfirm} variant="contained" color="primary" disabled={diagnosticsRunning}>
            {diagnosticsRunning ? 'Fixing Issues...' : 'Fix HTML Issues'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HTMLEditorChatbot; 
